import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { DrawerModule } from 'primeng/drawer';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';

import { MessageModule } from 'primeng/message';
import { ToastMessageService } from '@fitness-force/api';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import {
  Client,
  ClientSubscriptionFreezeResponse,
  CreateClientSubscriptionFreezeRequest,
  CreateFreezeDates,
  GetClientSubscriptionResponse,
  InvoiceOccurance,
} from '@fitness-force/models';
import {
  AuthSevices,
  ClientService,
  FreezeService,
  TimeconversionService,
} from '@fitness-force/api';
import { COMPANY_INFO } from 'apps/ff-frontend/src/app/state/app.state';
import { FreezeListComponent } from '../freeze-list/freeze-list.component';
import {
  PlanCategory,
  RecurringOccuranceType,
  Roles,
} from '@fitness-force/enum';
import { CommmonPipeModule } from '@fitness-force/pipes/common-pipes';
import { ErrorsComponent } from '@fitness-force/common/errors';
import { ShortNamePipe } from '@fitness-force/pipes/short-name';
import { AppErrors, ErrorMsgComponent } from '@fitness-force/errors';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'freeze-sidebar',
  templateUrl: './freeze-sidebar.component.html',
  styleUrls: ['./freeze-sidebar.component.scss'],
  providers: [ToastMessageService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    StyleClassModule,
    DrawerModule,
    AvatarModule,
    SelectModule,
    ButtonModule,
    DatePickerModule,
    TableModule,
    MultiSelectModule,
    MessageModule,
    CommmonPipeModule,
    ShortNamePipe,
    ErrorsComponent,
    ErrorMsgComponent,
    FreezeListComponent,
    TranslateModule
]
})
export class FreezeSidebarComponent implements OnInit {
  @Input('visible') visible: boolean = false;
  @Input('client') client: Client;
  @Input('cancelBookingType') cancelBookingType: string = '';
  @Input('subscription') subscription: GetClientSubscriptionResponse;
  @Output('onCloseSideBar') hideSideBar: EventEmitter<boolean> = new EventEmitter();

  showSidebar1 = true;
  fg: UntypedFormGroup;

  freezeTimeline$: BehaviorSubject<InvoiceOccurance[]> = new BehaviorSubject<InvoiceOccurance[]>([]);
  filteredFreezeTimeline$: BehaviorSubject<InvoiceOccurance[]> = new BehaviorSubject<InvoiceOccurance[]>([]);
  freeze$: BehaviorSubject<ClientSubscriptionFreezeResponse[]> = new BehaviorSubject<ClientSubscriptionFreezeResponse[]>([]);
  clientId$: BehaviorSubject<number> = new BehaviorSubject(0);
  isUpdated$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  currentUserDetails: any;

  error = new BehaviorSubject(null as AppErrors | null);
  showErrorPopup = new BehaviorSubject(false);
  errorMessages = [] as any;

  minDate: Date;
  maxDate: Date;

  btnLoading = new BehaviorSubject(false);
  freezeDays: number = 0;
  selectionLimit: number = 10;
  format: string = 'yyyy-MM-ddTHH:mm:ss';


  constructor(
    private freezeService: FreezeService,
    private clientService: ClientService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
    private store: Store,
    private fb: UntypedFormBuilder,
    private toastMessageService: ToastMessageService,
    public authService: AuthSevices,
    private timeService: TimeconversionService
  ) { }

  ngOnInit(): void {
    this.minDate = new Date();
    this.maxDate = new Date();
    this.currentUserDetails = this.authService.decodedToken;
    this.componentInit();
  }

  componentInit() {
    this.store.select(COMPANY_INFO).subscribe((responseData) => {
      this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
      this.translate.use(responseData?.selectedLang?.LANG_CODE);
    });

    if (this.IsRecurringSubscription) {
      this.fg = this.fb.group({
        selectedfreezeattempt: new UntypedFormControl('', [
          Validators.required,
        ]),
        comments: new UntypedFormControl(''),
      });
    } else {
      this.fg = this.fb.group({
        startdate: new UntypedFormControl('', [Validators.required]),
        enddate: new UntypedFormControl('', [Validators.required]),
        comments: new UntypedFormControl(''),
      });
    }

    this.clientId$.next(this.client.clt_id);
    this.GetFreezeTimeline(this.client.clt_id, this.subscription.id);
    this.GetFreezeBySubscriptionId(this.client.clt_id, this.subscription.id);

    this.StartDateControl?.valueChanges.subscribe((value: any) => {
      this.maxDate = value;
    });

    this.SelectedFreezeAttempt?.valueChanges.subscribe(selectedOptions => {
      if (selectedOptions && selectedOptions.length > 0) {
        const yearCounts: any = {};

        selectedOptions.forEach((option: InvoiceOccurance) => {
          const year = new Date(option.occurance_start_date).getFullYear();
          yearCounts[year] = (yearCounts[year] || 0) + 1;
        });

        for (const year in yearCounts) {
          const freezeYears = this.CycleRemainingInYear.find((o: any) => o.year == Number(year));
          if (freezeYears) {
            if (this.currentUserDetails.role.toLowerCase() == 'admin' ||
              this.currentUserDetails.role == Roles.Admin ||
              this.currentUserDetails.role.toString() == Roles[Roles.Admin])
              this.selectionLimit = 9999;
            else
              this.selectionLimit = this.subscription.freeze_allowed_number_of_attempts_in_year - freezeYears.count;
          }
        }
      }
    });
  }

  GetFreezeTimeline(clientId: number, subscriptionId: number): void {
    this.clientService.getPaymentTimeline(clientId, subscriptionId).subscribe({
      next: (freezeTimeline: any) => {
        if (freezeTimeline && freezeTimeline.invoice_occurance) {
          const timeline: InvoiceOccurance[] = [];
          const filteredData = (
            freezeTimeline.invoice_occurance as InvoiceOccurance[]
          ).filter(
            (o) =>
              (o.occurance_invoice_id == 0 && o.occurance_type != RecurringOccuranceType.Deposit &&
                o.occurance_type.toString() != RecurringOccuranceType[RecurringOccuranceType.Deposit]) ||
              ((o.occurance_type == RecurringOccuranceType.Deposit ||
                o.occurance_type.toString() == RecurringOccuranceType[RecurringOccuranceType.Deposit]) &&
                o.deposit_applied == false)
          );
          filteredData.forEach((obj: InvoiceOccurance) => {
            const newObj: InvoiceOccurance = obj;
            newObj.is_frozen = obj.occurance_invoice_id == 0 && obj.occurance_type == RecurringOccuranceType.Freeze ? true : false;
            const datetoutc =  this.timeService.ConvertUTCToLocalDateTime(obj.occurance_start_date);
            newObj.formated_start_date = this.toDate(datetoutc);
            const dateStr = new Date(datetoutc).toDateString();
            const dateStrArr = dateStr.split(' ');

            const s = ['th', 'st', 'nd', 'rd'];
            const v: number = parseInt(dateStrArr[2]) % 100;
            newObj.formated_date = `${dateStrArr[2].toString() + (s[(v - 20) % 10] || s[v] || s[0]).toString()} of ${dateStrArr[1]} ${dateStrArr[3]}`;
            timeline.push(newObj);
          });
          this.freezeTimeline$.next(
            freezeTimeline.invoice_occurance as InvoiceOccurance[]
          );
          this.filteredFreezeTimeline$.next(timeline as InvoiceOccurance[]);
          this.cd.detectChanges();
        } else {
          this.freezeTimeline$.next([]);
          this.filteredFreezeTimeline$.next([]);
          this.cd.detectChanges();
        }
      },
      error: (err) => {
        this.freezeTimeline$.next([]);
        this.filteredFreezeTimeline$.next([]);
        this.cd.detectChanges();
      },
    });
  }

  GetFreezeBySubscriptionId(clientId: number, subscriptionId: number): void {
    this.freezeService
      .GetFreezeBySubscriptionId(
        this.subscription.tenant_id,
        clientId,
        subscriptionId
      )
      .subscribe({
        next: (availFreeze: ClientSubscriptionFreezeResponse[]) => {
          if (availFreeze && availFreeze.length) this.freeze$.next(availFreeze);
          else this.freeze$.next([]);
          this.cd.detectChanges();
        },
        error: (err) => {
          this.freeze$.next([]);
          this.cd.detectChanges();
        },
      });
  }

  toDate(d: Date | string): string {
    const dateStr = new Date(d).toDateString();
    const dateStrArr = dateStr.split(' ');
    return `<span>${this.ordinalNumber(
      dateStrArr[2] as unknown as number
    )}</span>
    <span> of </span>
    <span>${dateStrArr[1]}</span>
    <span>${dateStrArr[3]}</span>`;
  }

  toDateWithInfo(invoiceOccurence: InvoiceOccurance): string {
    const dateStr = new Date(invoiceOccurence.occurance_start_date).toDateString();
    const isFrozenString = invoiceOccurence.occurance_type == RecurringOccuranceType.Freeze || invoiceOccurence.occurance_type.toString() == RecurringOccuranceType[RecurringOccuranceType.Freeze] ? '(This occurence is frozen)' : '';
    const dateStrArr = dateStr.split(' ');
    return `<span>${this.ordinalNumber(
      dateStrArr[2] as unknown as number
    )}</span>
    <span> of </span>
    <span>${dateStrArr[1]}</span>
    <span>${dateStrArr[3]}</span> 
    ${isFrozenString}`;
  }

  calculateDays() {
    if (this.StartDateControl?.value && this.EndDateControl?.value) {
      const startDate = new Date(this.StartDateControl?.value);
      const endDate = new Date(this.EndDateControl?.value);
      startDate.setHours(0, 0, 0);
      endDate.setHours(23, 59, 59);
      const startDateTime = new Date(startDate).getTime();
      const endDateTime = new Date(endDate).getTime();
      const timeDifference = endDateTime - startDateTime;
      const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
      this.freezeDays = daysDifference;
      return this.freezeDays;
    } else {
      this.freezeDays = 0;
      return this.freezeDays;
    }
  }

  ordinalNumber(n: number | null | undefined): string {
    if (n) {
      const s = ['th', 'st', 'nd', 'rd'];
      const v: number = n % 100;
      return (
        n.toString() +
        '<sup>' +
        (s[(v - 20) % 10] || s[v] || s[0]).toString() +
        '</sup> '
      );
    } else return '';
  }

  client2LetterName(name: string): string {
    if (name) {
      const nameArray: string[] = name.split(' ');
      const newNameArray: string[] = [];
      for (let i = 0; i < nameArray.length; i++) {
        if (newNameArray.length >= 2) break;

        if (nameArray[i]) {
          newNameArray.push(nameArray[i]);
        }
      }
      return newNameArray.join(' ');
    } else {
      return '';
    }
  }

  clientStatus(client: Client | null): string {
    if (client && (client.clt_status || (client as any)?.client_status))
      return this.clientService.getClientStatusHtml(
        (client.clt_status || '').toString()
      );
    else return '';
  }

  HideSidebar(): void {
    this.visible = false;
    this.hideSideBar.emit(this.isUpdated$.value);
  }

  IsUpdated(isUpdated: boolean): void {
    if (isUpdated) {
      this.GetFreezeTimeline(this.client.clt_id, this.subscription.id);
      this.GetFreezeBySubscriptionId(this.client.clt_id, this.subscription.id);
      this.isUpdated$.next(isUpdated);
    }
  }

  toDateString(date: Date | string): string {
    date = new Date(date);
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    ).toISOString();
  }

  createFreeze(): void {
    this.fg.markAllAsTouched();
    const request: CreateClientSubscriptionFreezeRequest = {
      dates: [],
      comment: '',
      cancel_appt_class_booking: '',
    };
    request.cancel_appt_class_booking = this.cancelBookingType;
    if (this.fg.valid) {
      this.btnLoading.next(true);
      const dates: CreateFreezeDates[] = [];
      if (this.IsRecurringSubscription) {
        this.SelectedFreezeAttempt?.value.forEach((d: any) => {
          dates.push({ start_date: this.getConvertedDate(d.occurance_start_date), end_date: this.getConvertedDate(d.occurance_end_date), occurance: d.occurance });
        });
      } else {
        const startDate = this.StartDateControl?.value;
        const endDate = this.EndDateControl?.value;
        endDate.setHours(23, 59, 59);
        dates.push({
          start_date: this.timeService.ConvertLocalDateTimeToUTC(startDate),
          end_date: this.timeService.ConvertLocalDateTimeToUTC(endDate),
          occurance: 0
        });
      }

      request.dates = dates;
      request.comment = this.fg.controls.comments.value;
      this.freezeService
        .PostCreateFreeze(
          this.subscription.tenant_id,
          this.client.clt_id,
          this.subscription.id,
          request
        )
        .subscribe({
          next: (obj) => {
            this.isUpdated$.next(true);
            this.translate
              .get('FREEZE.FREEZE_SIDEBAR.REQUEST_SUCCESS')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.visible = false;
            this.btnLoading.next(false);
            this.hideSideBar.emit(this.isUpdated$.value);
          },
          error: (error) => {
            this.btnLoading.next(false);
            this.error.next(error);
            this.showErrorPopup?.next(true);
          },
        });
    }
  }

  HideErrorPopup(event: any) {
    this.showErrorPopup?.next(false);
    this.errorMessages = [];
  }

  getConvertedDate(date: any) {
    const localDate = this.timeService.ConvertUTCToLocalDateTime(date);
    return this.timeService.ConvertLocalDateTimeToUTC(localDate);
  }

  get StartDateControl(): AbstractControl | null {
    return this.fg.controls.startdate as AbstractControl;
  }

  get EndDateControl(): AbstractControl | null {
    return this.fg.controls.enddate as AbstractControl;
  }

  get SelectedFreezeAttempt(): AbstractControl | null {
    return this.fg.controls.selectedfreezeattempt as AbstractControl;
  }

  get CycleRemainingInYear(): any[] {
    let billingOccurance: InvoiceOccurance[] = [];
    const yearWiseFreezeCount: any[] = [];
    if (this.freezeTimeline$ && this.freezeTimeline$.value)
      billingOccurance = this.freezeTimeline$.value;
    else billingOccurance = [];

    const years = billingOccurance
      .map((item) => new Date(item.occurance_start_date).getFullYear())
      .filter((value, index, self) => self.indexOf(value) === index);
    years.map((year: number) => {
      const cnt = billingOccurance.filter((bo) => {
        return (
          new Date(bo.occurance_start_date).getFullYear() == year &&
          (bo.occurance_type == RecurringOccuranceType.Freeze ||
            bo.occurance_type.toString() ==
            RecurringOccuranceType[RecurringOccuranceType.Freeze])
        );
      }).length;
      yearWiseFreezeCount.push({ year: year, count: cnt });
    });

    return yearWiseFreezeCount;
  }

  get IsRecurringSubscription(): boolean {
    return (
      this.subscription.subscription_category == PlanCategory.Contract ||
      this.subscription.subscription_category.toString() ==
      PlanCategory[PlanCategory.Contract] ||
      this.subscription.subscription_category == PlanCategory.PayAsYouGo ||
      this.subscription.subscription_category.toString() ==
      PlanCategory[PlanCategory.PayAsYouGo]
    );
  }
}
